import { useCallback, useEffect, useState, type FormEvent } from "react";
import Field from "../../components/Field";
import TextArea from "../../components/TextArea";
import Select from "../../components/Select";
import Modal from "../../components/Modal";
import FormMessage from "../../components/FormMessage";
import BookmarkRating from "../../components/BookmarkRating";
import PhotoUpload from "../../components/PhotoUpload";
import { ApiError, apiFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Bucket = "CURRENT" | "UP_NEXT" | "PAST";

type Book = {
  id: string;
  title: string;
  author: string;
  coverUrl: string | null;
  bucket: Bucket;
  monthLabel: string | null;
  meetingDate: string | null;
  description: string | null;
  clubRating: number | null;
  sortOrder: number;
};

type ListPayload = {
  current: Book | null;
  upNext: Book[];
  past: Book[];
};

export default function Reading() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [data, setData] = useState<ListPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Book | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const d = await apiFetch<ListPayload>("/api/books");
      setData(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load books.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function setRating(book: Book, next: number | null) {
    try {
      await apiFetch(`/api/books/${book.id}`, {
        method: "PATCH",
        body: { clubRating: next },
      });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save rating.");
    }
  }

  async function remove(book: Book) {
    if (!confirm(`Delete "${book.title}"?`)) return;
    try {
      await apiFetch(`/api/books/${book.id}`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete.");
    }
  }

  return (
    <>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          marginBottom: "2rem",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="eyebrow">Currently reading</div>
          <h1
            className="display display--lg"
            style={{ marginTop: "0.5rem", marginBottom: 0 }}
          >
            on the nightstand.
          </h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            + Add book
          </button>
        )}
      </header>

      {error && <FormMessage tone="error">{error}</FormMessage>}

      {!data ? (
        <p className="eyebrow">Loading…</p>
      ) : (
        <>
          {data.current ? (
            <CurrentBookCard
              book={data.current}
              isAdmin={isAdmin}
              onEdit={() => setEditing(data.current!)}
              onDelete={() => remove(data.current!)}
            />
          ) : (
            <EmptyCurrent isAdmin={isAdmin} onAdd={() => setCreating(true)} />
          )}

          <Rail
            title="Up next"
            books={data.upNext}
            empty="Nothing queued up yet."
            isAdmin={isAdmin}
            onEdit={(b) => setEditing(b)}
            onDelete={remove}
          />

          <Rail
            title="Past reads"
            books={data.past}
            empty="No past reads yet — finish a book together first."
            isAdmin={isAdmin}
            onEdit={(b) => setEditing(b)}
            onDelete={remove}
            renderExtra={(b) => (
              <div style={{ marginTop: "0.5rem" }}>
                <BookmarkRating
                  value={b.clubRating}
                  onChange={isAdmin ? (n) => setRating(b, n) : undefined}
                />
              </div>
            )}
          />
        </>
      )}

      {(creating || editing) && (
        <BookFormModal
          initial={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

// ---------- Current book hero ----------

function CurrentBookCard({
  book,
  isAdmin,
  onEdit,
  onDelete,
}: {
  book: Book;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <section
      style={{
        background: "var(--c-blush)",
        borderRadius: "var(--r-lg)",
        padding: "clamp(1.5rem, 4vw, 2.25rem)",
        marginBottom: "3rem",
        display: "grid",
        gridTemplateColumns: "minmax(140px, 220px) 1fr",
        gap: "clamp(1.25rem, 4vw, 2.5rem)",
        alignItems: "start",
        position: "relative",
      }}
    >
      <BookCover book={book} large />
      <div>
        {book.monthLabel && (
          <div className="eyebrow" style={{ color: "var(--c-palm)" }}>
            {book.monthLabel}
          </div>
        )}
        <h2
          className="display"
          style={{
            fontSize: "clamp(2rem, 5vw, 3rem)",
            margin: "0.4rem 0 0.25rem",
            lineHeight: 1.05,
          }}
        >
          {book.title.toLowerCase()}
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: "var(--f-body)",
            color: "var(--c-palm)",
            opacity: 0.85,
          }}
        >
          by {book.author}
        </p>
        {book.meetingDate && (
          <p
            style={{
              marginTop: "0.85rem",
              marginBottom: 0,
              fontSize: "0.95rem",
              color: "var(--c-palm)",
            }}
          >
            <strong>We meet:</strong>{" "}
            {new Date(book.meetingDate).toLocaleString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </p>
        )}
        {book.description && (
          <p
            style={{
              marginTop: "1rem",
              lineHeight: 1.6,
              color: "var(--c-ink)",
            }}
          >
            {book.description}
          </p>
        )}
        {isAdmin && (
          <AdminControls
            style={{ marginTop: "1.25rem" }}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}
      </div>
    </section>
  );
}

function EmptyCurrent({
  isAdmin,
  onAdd,
}: {
  isAdmin: boolean;
  onAdd: () => void;
}) {
  return (
    <section
      style={{
        background: "var(--c-blush)",
        borderRadius: "var(--r-lg)",
        padding: "clamp(2rem, 5vw, 3rem)",
        textAlign: "center",
        marginBottom: "3rem",
      }}
    >
      <h2
        className="display"
        style={{ fontSize: "clamp(1.6rem, 4vw, 2.2rem)", margin: 0 }}
      >
        no current book yet.
      </h2>
      <p style={{ margin: "0.75rem 0 1.25rem", color: "var(--c-palm)" }}>
        {isAdmin
          ? "Add one to set the tone for the month."
          : "An admin will pick one soon."}
      </p>
      {isAdmin && (
        <button type="button" className="btn btn--primary" onClick={onAdd}>
          + Add current book
        </button>
      )}
    </section>
  );
}

// ---------- Rails (Up Next, Past) ----------

function Rail({
  title,
  books,
  empty,
  isAdmin,
  onEdit,
  onDelete,
  renderExtra,
}: {
  title: string;
  books: Book[];
  empty: string;
  isAdmin: boolean;
  onEdit: (b: Book) => void;
  onDelete: (b: Book) => void;
  renderExtra?: (b: Book) => React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: "3rem" }}>
      <h2
        className="display"
        style={{
          fontSize: "clamp(1.5rem, 3.5vw, 2.2rem)",
          marginTop: 0,
          marginBottom: "1.25rem",
        }}
      >
        {title.toLowerCase()}
      </h2>
      {books.length === 0 ? (
        <p style={{ color: "var(--c-palm)", opacity: 0.7 }}>{empty}</p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gap: "1.25rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          }}
        >
          {books.map((b) => (
            <li
              key={b.id}
              style={{
                background: "#fff",
                border: "1px solid var(--c-rule)",
                borderRadius: "var(--r-md)",
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <BookCover book={b} />
              <div style={{ flex: 1 }}>
                {b.monthLabel && (
                  <div
                    className="eyebrow"
                    style={{ color: "var(--c-coral)", fontSize: "0.65rem" }}
                  >
                    {b.monthLabel}
                  </div>
                )}
                <div
                  style={{
                    fontWeight: 600,
                    color: "var(--c-palm)",
                    marginTop: 4,
                  }}
                >
                  {b.title}
                </div>
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                  {b.author}
                </div>
                {renderExtra?.(b)}
              </div>
              {isAdmin && (
                <AdminControls
                  onEdit={() => onEdit(b)}
                  onDelete={() => onDelete(b)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BookCover({ book, large = false }: { book: Book; large?: boolean }) {
  const ratio = large ? "2/3" : "2/3";
  if (book.coverUrl) {
    return (
      <img
        src={book.coverUrl}
        alt={`Cover of ${book.title}`}
        style={{
          width: "100%",
          aspectRatio: ratio,
          objectFit: "cover",
          borderRadius: "var(--r-md)",
          boxShadow: "var(--shadow-soft)",
        }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        width: "100%",
        aspectRatio: ratio,
        background: "var(--c-palm)",
        color: "var(--c-sand)",
        borderRadius: "var(--r-md)",
        display: "grid",
        placeItems: "center",
        padding: "1rem",
        textAlign: "center",
        fontFamily: "var(--f-display)",
        fontStyle: "italic",
        fontWeight: 900,
        fontSize: large ? "1.6rem" : "1rem",
        lineHeight: 1.1,
        textTransform: "lowercase",
        boxShadow: "var(--shadow-soft)",
      }}
    >
      {book.title.toLowerCase()}
    </div>
  );
}

function AdminControls({
  onEdit,
  onDelete,
  style,
}: {
  onEdit: () => void;
  onDelete: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ display: "flex", gap: "0.5rem", ...style }}>
      <button
        type="button"
        onClick={onEdit}
        className="btn btn--ghost"
        style={{ padding: "0.45rem 0.85rem", fontSize: "0.7rem" }}
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onDelete}
        style={{
          padding: "0.45rem 0.85rem",
          fontSize: "0.7rem",
          fontFamily: "var(--f-label)",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: 700,
          color: "var(--c-coral)",
          background: "transparent",
        }}
      >
        Delete
      </button>
    </div>
  );
}

// ---------- Add / edit modal ----------

function BookFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Book;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [author, setAuthor] = useState(initial?.author ?? "");
  const [bucket, setBucket] = useState<Bucket>(initial?.bucket ?? "UP_NEXT");
  const [coverUrl, setCoverUrl] = useState<string | null>(
    initial?.coverUrl ?? null,
  );
  const [monthLabel, setMonthLabel] = useState(initial?.monthLabel ?? "");
  const [meetingDate, setMeetingDate] = useState(
    initial?.meetingDate
      ? new Date(initial.meetingDate).toISOString().slice(0, 16)
      : "",
  );
  const [description, setDescription] = useState(initial?.description ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        author: author.trim(),
        bucket,
        coverUrl: coverUrl,
        monthLabel: monthLabel.trim() || null,
        meetingDate: meetingDate
          ? new Date(meetingDate).toISOString()
          : null,
        description: description.trim() || null,
      };
      if (initial) {
        await apiFetch(`/api/books/${initial.id}`, {
          method: "PATCH",
          body,
        });
      } else {
        await apiFetch("/api/books", { method: "POST", body });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={initial ? "Edit book" : "Add book"}>
      {error && <FormMessage tone="error">{error}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Field
          label="Author"
          required
          value={author}
          onChange={(e) => setAuthor(e.currentTarget.value)}
        />
        <Select
          label="Bucket"
          required
          value={bucket}
          onChange={(e) => setBucket(e.currentTarget.value as Bucket)}
          options={[
            { value: "CURRENT", label: "Currently reading" },
            { value: "UP_NEXT", label: "Up next" },
            { value: "PAST", label: "Past read" },
          ]}
          hint={
            bucket === "CURRENT"
              ? "Sets this as the current read. The previous current book moves to past."
              : undefined
          }
        />
        <PhotoUpload
          label="Cover image"
          value={coverUrl}
          onChange={setCoverUrl}
          shape="rect"
          aspectRatio="2/3"
          previewSize={120}
        />
        <Field
          label="Month label"
          value={monthLabel}
          onChange={(e) => setMonthLabel(e.currentTarget.value)}
          hint='Optional — e.g. "October 2026".'
        />
        <Field
          label="Meeting date & time"
          type="datetime-local"
          value={meetingDate}
          onChange={(e) => setMeetingDate(e.currentTarget.value)}
        />
        <TextArea
          label="Description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          maxLength={4000}
        />
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            justifyContent: "flex-end",
            marginTop: "0.5rem",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn--ghost"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
